import React, { ReactNode, useCallback } from "react";
import DriverHomeBg from "@/assets/backgrounds/DriverHomeBg.svg";
import BaseCar from '@/assets/illustrations/BaseCar.png';
import LoginErrorIcon from '@/assets/icons/LoginErrorIcon.svg';
import i18n from "@/lang/i18n";
import HomeBg from "@/assets/backgrounds/PrimaryCurveBg.svg";
import { Button } from "../ui/button";
import { ProfileHover } from "./ProfileHover";
import { useHubDetails, useSupervisorDetails, useUserDetails } from "@/redux/hooks/useSupervisorDetails";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { UserAdditionalProps } from "@/central-atoms/users/types";
import { convertTo12HourFormat } from "@/lib/utils";
import DriverProfileCard from "./DriverProfileCard";
import { clearAuth } from "@/redux/authSlice";
import { useNavigate } from "react-router-dom";
import { removeActiveFleetId } from "@/utils/appInitializer";

interface ErrorMessageFullCardProps {
    errorString: string[],
    handleBackToHome: () => void,
    children?: ReactNode
}
const ErrorMessageFullCard: React.FC<ErrorMessageFullCardProps> = ({ errorString, handleBackToHome, children }) => {
    const userDetails = useUserDetails();
    const supervisorDetails = useSupervisorDetails();
    const hubDetails = useHubDetails();
    const driverDetails = useSelector(
        (state: RootState) => state.driverDetails.details?.data
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        dispatch(clearAuth());
        localStorage.removeItem("auth_token");
        removeActiveFleetId();
        navigate("/signin");
    }, [dispatch, navigate]);

    const {
        user,
        reporting_time: reportingTime,
        login_token: loginToken,
        status: allocationStatus,
        disable_login: disableLogin,
    } = driverDetails || {};

    const userName = user?.name;
    const { external_user_props: externalUserProps } =
        (user?.additional_props as UserAdditionalProps) || {};
    const hub = externalUserProps?.driver_hub;
    const driverId = externalUserProps?.driver_id;
    return (
        <div className="absolute w-full h-full z-20 bg-white">
            {
                (userDetails && supervisorDetails?.data.hub_list) ? (
                    <div className="relative w-full">
                        <img
                            src={HomeBg}
                            alt="Background"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 items-center flex flex-row justify-between px-6">
                                <ProfileHover
                                    name={userDetails.name}
                                    hubList={supervisorDetails.data.hub_list.map((hub) => ({
                                        name: hub.name,
                                    }))}
                                    selectedHub={hubDetails?.name || ""}
                                    phone={userDetails.phone}
                                    hubSelectionDisabled
                                />
                        </div>
                    </div>
                )
                :
                    null
            }
            {
                (
                    driverDetails?.user ? (
                        <div className="relative w-full h-[140px]">
                            <div className="absolute w-full px-[20px] pt-[20px]">
                                <DriverProfileCard
                                    handleLogout={handleLogout}
                                    statusType={disableLogin ? "danger" : "safe"}
                                    name={userName || ""}
                                    hub={hub || ""}
                                    driverId={driverId || ""}
                                    entryType={loginToken ? "login_token" : "report_at"}
                                    entryTypeText={
                                        loginToken || convertTo12HourFormat(reportingTime || "")
                                    }
                                    driverStatus={allocationStatus || ""}
                                    showNecessaryInfoOnly
                                />
                            </div>
                        </div>
                    )
                    :
                        null
                )
            }
            <div className="px-[24px] flex items-center justify-center h-[90vh]">
                <div className="relative flex flex-col items-center justify-center w-full min-h-[176px] border border-[#FA0404] rounded-[30px] shadow-[0px_2px_0px_#FA0404] p-[24px] pt-[58px]">
                    <div className="absolute -top-[140px] flex items-center justify-center rounded-[100px] w-[126px] h-[126px] border border-[12px] border-[#fa04041f] shadow-[0px_4px_24px_22px_#fa04040a]">
                        <img alt="login error icon mt-[20px]" src={LoginErrorIcon} />
                    </div>
                    <img alt="basic car image" src={BaseCar} className="absolute -top-[60px] w-[250px]" />
                    <div className="w-full h-full pb-[18px] text-secondary">
                        {errorString.map(s => <p className="text-center">{s}</p>)}
                    </div>
                    {
                        children
                    }
                    <Button className="rounded-[10px] w-full bg-[#FA0404] text-[14px] h-[47px]" onClick={handleBackToHome}>Back</Button>
                </div>
            </div>
        </div>
    );
};

export default ErrorMessageFullCard;
